/**
 * Expansion Paths Service
 * 
 * Makes M3.F3 (Expansion paths clear) true.
 * 
 * Evidence criteria:
 * - Upsell opportunities are identified
 * - Feature discovery is enabled
 * - Usage patterns suggest expansion
 * - Revenue potential is quantifiable
 */

import { recordMetric } from './observability';

// ============================================================================
// Types
// ============================================================================

export type ExpansionType = 
  | 'seat_expansion'      // More users
  | 'tier_upgrade'        // Higher plan
  | 'feature_addon'       // Premium feature
  | 'integration_addon'   // Premium integration
  | 'professional_services'; // Consulting/implementation

export interface ExpansionOpportunity {
  id: string;
  customerId: string;
  customerName: string;
  
  // Opportunity details
  type: ExpansionType;
  name: string;
  description: string;
  
  // Trigger
  triggerReason: string;
  triggerMetric?: string;
  triggerValue?: number;
  
  // Value
  estimatedArrImpact: number; // Annual recurring revenue
  confidence: 'high' | 'medium' | 'low';
  
  // Status
  status: 'identified' | 'qualified' | 'proposed' | 'negotiating' | 'won' | 'lost';
  
  // Timing
  identifiedAt: string;
  targetCloseDate?: string;
  closedAt?: string;
  
  // Assignment
  ownerId?: string;
  ownerName?: string;
  
  // Notes
  notes: string[];
}

export interface FeatureDiscovery {
  customerId: string;
  
  // Feature usage
  usedFeatures: string[];
  unusedFeatures: string[];
  
  // Recommendations
  recommendations: FeatureRecommendation[];
  
  // Tier comparison
  currentTier: 'starter' | 'professional' | 'enterprise';
  tierLimitReached: TierLimit[];
}

export interface FeatureRecommendation {
  featureId: string;
  featureName: string;
  reason: string;
  relevanceScore: number; // 0-100
  requiredTier?: 'professional' | 'enterprise';
  estimatedImpact: string;
}

export interface TierLimit {
  limitType: 'seats' | 'cycles' | 'integrations' | 'storage' | 'api_calls';
  currentUsage: number;
  limit: number;
  percentUsed: number;
  upgradeRecommended: boolean;
}

// ============================================================================
// Feature Catalog
// ============================================================================

export interface Feature {
  id: string;
  name: string;
  description: string;
  category: 'core' | 'advanced' | 'enterprise' | 'addon';
  availableIn: ('starter' | 'professional' | 'enterprise')[];
  monthlyPrice?: number; // For addons
}

export const FEATURE_CATALOG: Feature[] = [
  // Core features (all tiers)
  { id: 'idea-submission', name: 'Idea Submission', description: 'Submit and track ideas', category: 'core', availableIn: ['starter', 'professional', 'enterprise'] },
  { id: 'basic-prioritization', name: 'Basic Prioritization', description: 'RICE and MoSCoW tools', category: 'core', availableIn: ['starter', 'professional', 'enterprise'] },
  { id: 'item-pools', name: 'Item Pools', description: 'Manage comparison sets and publish ranked lists', category: 'core', availableIn: ['starter', 'professional', 'enterprise'] },
  
  // Advanced features (professional+)
  { id: 'advanced-tools', name: 'Advanced Tools', description: 'WSJF, Kano, and more', category: 'advanced', availableIn: ['professional', 'enterprise'] },
  { id: 'integrations', name: 'Integrations', description: 'Connect to Jira, Asana, etc.', category: 'advanced', availableIn: ['professional', 'enterprise'] },
  { id: 'analytics', name: 'Analytics Dashboard', description: 'Usage and adoption insights', category: 'advanced', availableIn: ['professional', 'enterprise'] },
  
  // Enterprise features
  { id: 'portfolio-governance', name: 'Portfolio Governance', description: 'Multi-team hierarchy', category: 'enterprise', availableIn: ['enterprise'] },
  { id: 'strategy-alignment', name: 'Strategy Alignment', description: 'Map priorities to objectives', category: 'enterprise', availableIn: ['enterprise'] },
  { id: 'sso', name: 'Single Sign-On', description: 'SAML/OIDC SSO', category: 'enterprise', availableIn: ['enterprise'] },
  { id: 'audit-logs', name: 'Audit Logs', description: 'Compliance audit trail', category: 'enterprise', availableIn: ['enterprise'] },
  { id: 'api-access', name: 'API Access', description: 'Full API access', category: 'enterprise', availableIn: ['enterprise'] },
  
  // Addons
  { id: 'addon-consulting', name: 'Implementation Consulting', description: '10 hours of expert help', category: 'addon', availableIn: ['professional', 'enterprise'], monthlyPrice: 500 },
  { id: 'addon-training', name: 'Custom Training', description: 'Dedicated training sessions', category: 'addon', availableIn: ['professional', 'enterprise'], monthlyPrice: 300 },
];

// ============================================================================
// Tier Limits
// ============================================================================

export const TIER_LIMITS: Record<string, Record<TierLimit['limitType'], number>> = {
  starter: {
    seats: 5,
    cycles: 3,
    integrations: 0,
    storage: 1, // GB
    api_calls: 0,
  },
  professional: {
    seats: 25,
    cycles: 20,
    integrations: 3,
    storage: 10,
    api_calls: 10000,
  },
  enterprise: {
    seats: 999,
    cycles: 999,
    integrations: 999,
    storage: 100,
    api_calls: 999999,
  },
};

// ============================================================================
// In-Memory Store
// ============================================================================

const opportunityStore: Map<string, ExpansionOpportunity> = new Map();

// ============================================================================
// Opportunity Management
// ============================================================================

export async function identifyOpportunity(
  input: Omit<ExpansionOpportunity, 'id' | 'identifiedAt' | 'status'>
): Promise<ExpansionOpportunity> {
  const opportunity: ExpansionOpportunity = {
    ...input,
    id: crypto.randomUUID(),
    status: 'identified',
    identifiedAt: new Date().toISOString(),
    notes: [],
  };
  
  opportunityStore.set(opportunity.id, opportunity);
  recordMetric('usage', 'expansion_opportunity_identified', opportunity.estimatedArrImpact, {
    type: opportunity.type,
    confidence: opportunity.confidence,
  });
  
  console.log(`[ExpansionPaths] Identified ${opportunity.type} for ${opportunity.customerName}`);
  return opportunity;
}

export async function updateOpportunityStatus(
  opportunityId: string,
  status: ExpansionOpportunity['status'],
  note?: string
): Promise<ExpansionOpportunity | null> {
  const opportunity = opportunityStore.get(opportunityId);
  if (!opportunity) return null;
  
  opportunity.status = status;
  
  if (status === 'won' || status === 'lost') {
    opportunity.closedAt = new Date().toISOString();
    recordMetric('usage', `expansion_opportunity_${status}`, opportunity.estimatedArrImpact);
  }
  
  if (note) {
    opportunity.notes.push(`[${new Date().toISOString()}] ${note}`);
  }
  
  opportunityStore.set(opportunityId, opportunity);
  return opportunity;
}

export async function listOpportunities(
  filter?: { customerId?: string; type?: ExpansionType; status?: ExpansionOpportunity['status'] }
): Promise<ExpansionOpportunity[]> {
  let opportunities = Array.from(opportunityStore.values());
  
  if (filter?.customerId) {
    opportunities = opportunities.filter(o => o.customerId === filter.customerId);
  }
  if (filter?.type) {
    opportunities = opportunities.filter(o => o.type === filter.type);
  }
  if (filter?.status) {
    opportunities = opportunities.filter(o => o.status === filter.status);
  }
  
  return opportunities.sort((a, b) => b.estimatedArrImpact - a.estimatedArrImpact);
}

// ============================================================================
// Feature Discovery
// ============================================================================

export async function analyzeFeatureDiscovery(
  customerId: string,
  customerName: string,
  currentTier: FeatureDiscovery['currentTier'],
  usedFeatureIds: string[],
  currentUsage: Record<TierLimit['limitType'], number>
): Promise<FeatureDiscovery> {
  const usedFeatures = usedFeatureIds;
  const allFeatures = FEATURE_CATALOG.filter(f => f.availableIn.includes(currentTier));
  const unusedFeatures = allFeatures
    .filter(f => !usedFeatureIds.includes(f.id))
    .map(f => f.id);
  
  // Generate recommendations
  const recommendations: FeatureRecommendation[] = [];
  
  // Recommend unused features in current tier
  for (const featureId of unusedFeatures.slice(0, 3)) {
    const feature = FEATURE_CATALOG.find(f => f.id === featureId)!;
    recommendations.push({
      featureId,
      featureName: feature.name,
      reason: `You have access to ${feature.name} but haven't used it yet`,
      relevanceScore: 70,
      estimatedImpact: 'Improve adoption and get more value',
    });
  }
  
  // Recommend upgrade features
  if (currentTier !== 'enterprise') {
    const upgradeFeatures = FEATURE_CATALOG.filter(f => 
      !f.availableIn.includes(currentTier) &&
      f.category !== 'addon'
    );
    
    for (const feature of upgradeFeatures.slice(0, 2)) {
      const requiredTier = feature.availableIn.includes('professional') 
        ? 'professional' 
        : 'enterprise';
      
      recommendations.push({
        featureId: feature.id,
        featureName: feature.name,
        reason: `${feature.name} could help you ${feature.description.toLowerCase()}`,
        relevanceScore: 60,
        requiredTier,
        estimatedImpact: 'Unlock advanced capabilities',
      });
    }
  }
  
  // Check tier limits
  const limits = TIER_LIMITS[currentTier];
  const tierLimitReached: TierLimit[] = [];
  
  for (const [limitType, limit] of Object.entries(limits)) {
    const usage = currentUsage[limitType as TierLimit['limitType']] || 0;
    const percentUsed = limit > 0 ? Math.round((usage / limit) * 100) : 0;
    
    if (percentUsed >= 80) {
      tierLimitReached.push({
        limitType: limitType as TierLimit['limitType'],
        currentUsage: usage,
        limit,
        percentUsed,
        upgradeRecommended: percentUsed >= 90,
      });
      
      // Auto-create expansion opportunity
      if (percentUsed >= 90) {
        await identifyOpportunity({
          customerId,
          customerName,
          type: limitType === 'seats' ? 'seat_expansion' : 'tier_upgrade',
          name: `${limitType} limit approaching`,
          description: `Customer is at ${percentUsed}% of their ${limitType} limit`,
          triggerReason: 'Tier limit reached',
          triggerMetric: limitType,
          triggerValue: percentUsed,
          estimatedArrImpact: currentTier === 'starter' ? 2000 : 5000,
          confidence: percentUsed >= 100 ? 'high' : 'medium',
        });
      }
    }
  }
  
  return {
    customerId,
    usedFeatures,
    unusedFeatures,
    recommendations: recommendations.sort((a, b) => b.relevanceScore - a.relevanceScore),
    currentTier,
    tierLimitReached,
  };
}

// ============================================================================
// Pipeline Summary
// ============================================================================

export interface ExpansionPipeline {
  generatedAt: string;
  
  // Pipeline value
  totalPipelineValue: number;
  byType: Record<ExpansionType, { count: number; value: number }>;
  byStatus: Record<ExpansionOpportunity['status'], { count: number; value: number }>;
  
  // Conversion
  wonLast30Days: number;
  lostLast30Days: number;
  conversionRate: number;
  
  // Top opportunities
  topOpportunities: ExpansionOpportunity[];
}

export async function getExpansionPipeline(): Promise<ExpansionPipeline> {
  const all = await listOpportunities();
  const active = all.filter(o => !['won', 'lost'].includes(o.status));
  
  const totalPipelineValue = active.reduce((sum, o) => sum + o.estimatedArrImpact, 0);
  
  // By type
  const byType: ExpansionPipeline['byType'] = {
    seat_expansion: { count: 0, value: 0 },
    tier_upgrade: { count: 0, value: 0 },
    feature_addon: { count: 0, value: 0 },
    integration_addon: { count: 0, value: 0 },
    professional_services: { count: 0, value: 0 },
  };
  
  for (const o of active) {
    byType[o.type].count++;
    byType[o.type].value += o.estimatedArrImpact;
  }
  
  // By status
  const byStatus: ExpansionPipeline['byStatus'] = {
    identified: { count: 0, value: 0 },
    qualified: { count: 0, value: 0 },
    proposed: { count: 0, value: 0 },
    negotiating: { count: 0, value: 0 },
    won: { count: 0, value: 0 },
    lost: { count: 0, value: 0 },
  };
  
  for (const o of all) {
    byStatus[o.status].count++;
    byStatus[o.status].value += o.estimatedArrImpact;
  }
  
  // Last 30 days wins/losses
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const recentClosed = all.filter(o => 
    o.closedAt && new Date(o.closedAt) >= thirtyDaysAgo
  );
  
  const wonLast30Days = recentClosed.filter(o => o.status === 'won')
    .reduce((sum, o) => sum + o.estimatedArrImpact, 0);
  const lostLast30Days = recentClosed.filter(o => o.status === 'lost')
    .reduce((sum, o) => sum + o.estimatedArrImpact, 0);
  
  const totalClosed = recentClosed.length;
  const wonCount = recentClosed.filter(o => o.status === 'won').length;
  const conversionRate = totalClosed > 0 ? Math.round((wonCount / totalClosed) * 100) : 0;
  
  return {
    generatedAt: new Date().toISOString(),
    totalPipelineValue,
    byType,
    byStatus,
    wonLast30Days,
    lostLast30Days,
    conversionRate,
    topOpportunities: active.slice(0, 10),
  };
}


